import { createBrowserRouter, Navigate, useLocation, useParams } from 'react-router-dom'
import { CookieConsent } from 'src/pages/GDPR/CookieConsent'
import { PrivacyPolicy } from 'src/pages/GDPR/PrivacyPolicy'
import { TermsOfService } from 'src/pages/GDPR/TermsOfService'
import { LoginSignup } from 'src/pages/LoginSignup'
import { SignupSuccess } from 'src/pages/SignupSuccess'
import { ProductsPage } from 'src/pages/_myAccount/Inventory/Products'
import { DecorationsCostsPage } from 'src/pages/_myAccount/Inventory/DecorationsCosts'
import { DecorationsInfoPage } from 'src/pages/_myAccount/Inventory/DecorationsInfo'
import { AddDecorationsPage } from 'src/pages/_myAccount/Inventory/Decorations/AddDecorations'
import { AddSharedDecorationsPage } from 'src/pages/_myAccount/Inventory/Decorations/AddSharedDecorations'
import { AddEditProductPage } from 'src/pages/_myAccount/Inventory/Products/AddEditProduct'
import { CategoriesPage } from 'src/pages/_myAccount/Inventory/Categories'
import { OrganizationsPage } from 'src/pages/_myAccount/Organizations'
import { OrganizationPage } from 'src/pages/_myAccount/Organizations/Organization'
import { AppLayout } from 'src/shared/components/navigation/AppLayout'
import { AdminLayout } from 'src/shared/components/navigation/AdminLayout'
import { AdminAppRedirect } from 'src/shared/components/navigation/AdminAppRedirect'
import { ProtectedRoute } from '../shared/components/navigation/ProtectedRoute'
import { paths, buildGuidedAssistantPath } from './paths'
import { HomePage } from 'src/pages/Home'
import { AboutPage } from 'src/pages/About'
import { PreviousWorkPage } from 'src/pages/PreviousWork'
import { ContactPage } from 'src/pages/Contact'
import { RequestsPage } from 'src/pages/_myAccount/Requests'
import { RequestPage } from 'src/pages/_myAccount/Requests/Request'
import { GuidedAssistantPage } from 'src/pages/GuidedAssistant'
import { MyAccountPage } from 'src/pages/_myAccount/Settings'
import { ProductsPage as OpenProductsPage } from 'src/pages/Products'
import { OurProductsPage } from 'src/pages/OurProducts'
import { ProductPage } from 'src/pages/Product'
import { EditDecorationPage } from 'src/pages/_myAccount/EditDecoration'
import { EditDecorationPageOpen } from 'src/pages/_myAccount/EditDecoration/OpenWrapper'
import { ShareOrderPage } from 'src/pages/ShareOrder'
import { DashboardPage } from 'src/pages/_myAccount/Dashboard'
import { FavouritesPage } from 'src/pages/_myAccount/Favourites'
import { InvoicesPage } from 'src/pages/_myAccount/Invoices'
import { RoleLandingPage } from 'src/pages/RoleLanding'
import { InvitationPage } from 'src/pages/Invitation'
import { MyAccountAnonAuthGate } from 'src/shared/components/MyAccountAnonAuthGate'
import { AdminDashboardPage } from 'src/pages/_admin/Dashboard'
import { AdminOrdersPage } from 'src/pages/_admin/Orders'
import { AdminOrderPage } from 'src/pages/_admin/Orders/Order'
import { AdminEmailPage } from 'src/pages/_admin/Email'
import { AdminInstructionsPage } from 'src/pages/_admin/Instructions'
import { AdminAnalyticsPage } from 'src/pages/_admin/Analytics'
import { AdminCompanyPage } from 'src/pages/_admin/Company'
import { AdminRequestsOverviewPage } from 'src/pages/_admin/RequestsOverview'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'

// Legacy /assistant and /chat URLs → requests list
// eslint-disable-next-line react-refresh/only-export-components
const LegacyAssistantRedirect = () => (
  <Navigate to={`${paths.myAccount}${paths.requests}`} replace />
)

// My Account layout with auth protection
// eslint-disable-next-line react-refresh/only-export-components
const MyAccountLayout = () => (
  <AppLayout
    navbar
    mode="user"
    variant="light"
    wrapOutlet={(node) => <MyAccountAnonAuthGate>{node}</MyAccountAnonAuthGate>}
  />
)

/** Staff landing on customer dashboard → admin dashboard */
// eslint-disable-next-line react-refresh/only-export-components
const DashboardOrAdminRedirect = () => {
  const isAdmin = useIsAdmin()
  if (isAdmin) {
    return <Navigate to={paths.admin} replace />
  }
  return <DashboardPage />
}

// Redirect /my-account/* → /app/* (legacy bookmarks and emails)
// eslint-disable-next-line react-refresh/only-export-components
const LegacyMyAccountRedirect = () => {
  const location = useLocation()
  const to =
    location.pathname.replace(
      new RegExp(`^${paths.myAccountLegacy}`),
      paths.myAccount
    ) +
    location.search +
    location.hash
  return <Navigate to={to} replace />
}

// Redirect component for /:org_id/* -> /app/quotes/id={org_id}
// Matches UUID pattern: 8-4-4-4-12 characters (e.g., d78df6a7-340f-46e2-aae7-a4ee9dddbb69)
// eslint-disable-next-line react-refresh/only-export-components
const OrgIdRedirect = () => {
  const { org_id } = useParams<{ org_id: string }>()
  // UUID pattern: 8 chars - 4 chars - 4 chars - 4 chars - 12 chars
  const uuidPattern =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

  if (org_id && uuidPattern.test(org_id)) {
    return <Navigate to={`${paths.myAccount}/quotes?id=${org_id}`} replace />
  }

  // If it doesn't match UUID pattern, redirect to home
  return <Navigate to="/" replace />
}

const historyRequestsRedirectTo = `${paths.myAccount}${paths.requests}?segment=delivered`
const historyItemsRedirectTo = `${paths.myAccount}${paths.requests}?segment=delivered&requestList=items`

// eslint-disable-next-line react-refresh/only-export-components
const HistoryPublicIdRedirect = () => {
  const { public_id } = useParams<{ public_id: string }>()
  return (
    <Navigate
      to={`${paths.myAccount}${paths.requests}/${public_id ?? ''}`}
      replace
    />
  )
}

// eslint-disable-next-line react-refresh/only-export-components
const OrdersLegacyRedirect = () => {
  const { public_id } = useParams<{ public_id?: string }>()
  const location = useLocation()
  const search = new URLSearchParams(location.search)
  if (search.has('orderList') && !search.has('requestList')) {
    const legacy = search.get('orderList')
    search.delete('orderList')
    if (legacy && legacy !== 'orders') {
      search.set('requestList', legacy)
    }
  }
  const query = search.toString()
  const suffix = query ? `?${query}` : ''
  if (public_id) {
    return (
      <Navigate
        to={`${paths.myAccount}${paths.requests}/${public_id}${suffix}`}
        replace
      />
    )
  }
  return (
    <Navigate
      to={`${paths.myAccount}${paths.requests}${suffix}`}
      replace
    />
  )
}

// eslint-disable-next-line react-refresh/only-export-components
const GuidedAssistantLegacyRedirect = () => {
  const { public_id, view } = useParams<{ public_id: string; view?: string }>()
  return (
    <Navigate
      to={buildGuidedAssistantPath(public_id ?? '', view)}
      replace
    />
  )
}

/** Preserve path under a moved admin prefix: /app/X/y → /admin/X/y */
// eslint-disable-next-line react-refresh/only-export-components
const LegacyAppStaffRedirect = () => {
  const location = useLocation()
  const rest = location.pathname.slice(paths.myAccount.length)
  return (
    <Navigate
      to={`${paths.admin}${rest}${location.search}${location.hash}`}
      replace
    />
  )
}

export const router = createBrowserRouter([
  // --- Open Website Routes ---
  {
    path: '/',
    element: <AppLayout navbar mode="user" variant="light" />,
    children: [
      {
        children: [
          {
            index: true,
            element: <HomePage />,
          },
          {
            path: 'c/:type/:email/:query',
            element: <HomePage />,
          },
          {
            path: paths.guidedAssistant.slice(1) + '/:public_id/:view?',
            element: <GuidedAssistantPage />,
          },
          {
            path: 'assistant/:chat_id?',
            element: <LegacyAssistantRedirect />,
          },
          {
            path: paths.allProducts.slice(1),
            element: <OurProductsPage />,
          },
          {
            path: paths.product + '/:product_id',
            element: <ProductPage />,
          },
          {
            path: paths.chat.slice(1) + '/:chat_id?',
            element: <LegacyAssistantRedirect />,
          },
          {
            path: paths.about,
            element: <AboutPage />,
          },
          {
            path: paths.previousWork,
            element: <PreviousWorkPage />,
          },
          {
            path: paths.contact,
            element: <ContactPage />,
          },
          {
            path: paths.forRole,
            element: <RoleLandingPage />,
          },
          {
            path: paths.cookieConsent,
            element: <CookieConsent />,
          },
          {
            path: paths.privacyPolicy,
            element: <PrivacyPolicy />,
          },
          {
            path: paths.privacyPolicyLegacy,
            element: <Navigate to={paths.privacyPolicy} replace />,
          },
          {
            path: paths.termsOfService,
            element: <TermsOfService />,
          },
          {
            path: paths.termsOfServiceLegacy,
            element: <Navigate to={paths.termsOfService} replace />,
          },
        ],
      },
    ],
  },

  // --- Admin (dashboard + staff ops) ---
  {
    path: paths.admin,
    element: <AdminLayout />,
    children: [
      {
        index: true,
        element: <AdminDashboardPage />,
      },
      {
        path: paths.adminRequestsOverview.slice(1),
        element: <AdminRequestsOverviewPage />,
      },
      {
        path: paths.adminOrders.slice(1),
        element: <AdminOrdersPage />,
      },
      {
        path: paths.adminOrders.slice(1) + '/:public_id/:view?',
        element: <AdminOrderPage />,
      },
      {
        path: 'organizations',
        element: <OrganizationsPage />,
      },
      {
        path: 'organizations/:org_id',
        element: <OrganizationPage />,
      },
      {
        path: 'products',
        element: <ProductsPage />,
      },
      {
        path: 'products/new',
        element: <AddEditProductPage />,
      },
      {
        path: 'products/:productId',
        element: <AddEditProductPage />,
      },
      {
        path: 'categories',
        element: <CategoriesPage />,
      },
      {
        path: 'decorations-costs',
        element: <DecorationsCostsPage />,
      },
      {
        path: 'decorations-info',
        element: <DecorationsInfoPage />,
      },
      {
        path: 'decorations/new',
        element: <AddDecorationsPage />,
      },
      {
        path: 'decorations/shared/new',
        element: <AddSharedDecorationsPage />,
      },
      {
        path: 'email',
        element: <AdminEmailPage />,
      },
      {
        path: 'inbox',
        element: <Navigate to={paths.adminEmail} replace />,
      },
      {
        path: 'instructions',
        element: <AdminInstructionsPage />,
      },
      {
        path: 'analytics',
        element: <AdminAnalyticsPage />,
      },
      {
        path: 'company',
        element: <AdminCompanyPage />,
      },
    ],
  },

  // --- My Account Routes (customer) ---
  {
    path: paths.myAccount,
    element: <MyAccountLayout />,
    children: [
      {
        children: [
          {
            index: true,
            element: <AdminAppRedirect />,
          },
          {
            path: paths.dashboard.slice(1),
            element: <DashboardOrAdminRedirect />,
          },
          {
            path: paths.requests.slice(1),
            element: <RequestsPage />,
          },
          {
            path: paths.requests.slice(1) + '/:public_id/:view?',
            element: <RequestPage />,
          },
          {
            path: paths.guidedAssistant.slice(1) + '/:public_id/:view?',
            element: <GuidedAssistantLegacyRedirect />,
          },
          {
            path: paths.orders.slice(1),
            element: <OrdersLegacyRedirect />,
          },
          {
            path: paths.orders.slice(1) + '/:public_id',
            element: <OrdersLegacyRedirect />,
          },
          {
            path: `${paths.requests.slice(
              1
            )}/:order_id/order_items/:order_item_id/sub_item/:order_sub_item_id`,
            element: <EditDecorationPage />,
          },
          {
            path: paths.inventory.slice(1) + '/new',
            element: <ProtectedRoute component={AddEditProductPage} />,
          },
          {
            path: paths.inventory.slice(1) + '/:productId',
            element: <ProtectedRoute component={AddEditProductPage} />,
          },
          {
            path:
              paths.history.slice(1) +
              paths.historyOrders.slice(paths.history.length),
            element: <Navigate to={historyRequestsRedirectTo} replace />,
          },
          {
            path:
              paths.history.slice(1) +
              paths.historyItems.slice(paths.history.length),
            element: <Navigate to={historyItemsRedirectTo} replace />,
          },
          {
            path: paths.history.slice(1) + '/:public_id',
            element: <HistoryPublicIdRedirect />,
          },
          {
            path: paths.history.slice(1),
            element: <Navigate to={historyRequestsRedirectTo} replace />,
          },
          {
            path: paths.favourites.slice(1),
            element: <FavouritesPage />,
          },
          {
            path: paths.catalog.slice(1),
            element: <OpenProductsPage showFooter={false} />,
          },
          {
            path: paths.invoices.slice(1),
            element: <InvoicesPage />,
          },
          {
            path: paths.assets.slice(1),
            element: (
              <Navigate
                to={`${paths.myAccount}${paths.settings}?tab=assets`}
                replace
              />
            ),
          },
          {
            path: paths.settings.slice(1),
            element: <ProtectedRoute component={MyAccountPage} />,
          },
          // Legacy /app staff routes → /admin
          {
            path: 'organizations/*',
            element: <LegacyAppStaffRedirect />,
          },
          {
            path: 'organizations',
            element: <Navigate to={paths.adminOrganizations} replace />,
          },
          {
            path: 'products/*',
            element: <LegacyAppStaffRedirect />,
          },
          {
            path: 'products',
            element: <Navigate to={paths.adminProducts} replace />,
          },
          {
            path: 'categories',
            element: <Navigate to={paths.adminCategories} replace />,
          },
          {
            path: 'decorations-costs',
            element: <Navigate to={paths.adminDecorationsCosts} replace />,
          },
          {
            path: 'decorations-info',
            element: <Navigate to={paths.adminDecorationsInfo} replace />,
          },
          {
            path: 'decorations/new',
            element: <Navigate to={paths.adminDecorationsNew} replace />,
          },
          {
            path: 'decorations/shared/new',
            element: (
              <Navigate to={paths.adminDecorationsSharedNew} replace />
            ),
          },
          {
            path: 'chats/*',
            element: <Navigate to={paths.admin} replace />,
          },
          {
            path: 'chats',
            element: <Navigate to={paths.admin} replace />,
          },
        ],
      },
    ],
  },

  // --- Open routes without navigation ---
  {
    path: '/',
    element: <AppLayout mode="user" />,
    children: [
      {
        path: `${paths.requests.slice(
          1
        )}/:order_id/order_items/:order_item_id/sub_item/:order_sub_item_id`,
        element: <EditDecorationPageOpen />,
      },
      {
        path: paths.loginSignUp,
        element: <LoginSignup />,
      },
      {
        path: paths.invitation,
        element: <InvitationPage />,
      },
      {
        path: paths.login,
        element: <Navigate to={`${paths.loginSignUp}?login`} replace />,
      },
      {
        path: paths.signup,
        element: <Navigate to={paths.loginSignUp} replace />,
      },
      {
        path: paths.signupSuccess,
        element: <ProtectedRoute component={SignupSuccess} />,
      },
      {
        path: '/share-order/:public_id',
        element: <ShareOrderPage />,
      },
    ],
  },

  // Legacy /my-account → /app (preserve path, query, hash)
  {
    path: `${paths.myAccountLegacy}/*`,
    element: <LegacyMyAccountRedirect />,
  },
  {
    path: paths.myAccountLegacy,
    element: <LegacyMyAccountRedirect />,
  },

  // Catch-all route for UUID-based org_id redirects
  // This should be last to avoid matching other routes
  {
    path: '/:org_id/*',
    element: <OrgIdRedirect />,
  },
])
